import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShoppingBag, FiMenu, FiX } = FiIcons;

export default function Navbar({ cartCount, onOpenCart }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Shop All', path: '/shop' },
    { name: 'Gift Sets', path: '/shop?category=gifts' },
    { name: 'Candle Care', path: '/care' },
    { name: 'Our Story', path: '/about' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-smoke/90 backdrop-blur-xl py-3 shadow-2xl border-b border-white/5' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
        
        <button 
          className="md:hidden text-linen/80 hover:text-ember transition-colors p-2 -ml-2" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
        </button>

        <div className="hidden md:flex items-center space-x-10">
          {navLinks.slice(0, 2).map((link) => (
            <Link key={link.name} to={link.path} className="text-[11px] tracking-[0.2em] uppercase text-linen/60 hover:text-ember transition-all duration-300 font-semibold relative group">
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-ember transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </div>

        <Link to="/" className="text-2xl md:text-3xl font-serif tracking-[0.15em] text-linen absolute left-1/2 -translate-x-1/2 hover:opacity-80 transition-opacity">
          EMBER<span className="text-ember italic font-normal">VALE</span>
        </Link>

        <div className="flex items-center space-x-6 md:space-x-10">
          <div className="hidden md:flex items-center space-x-10">
            {navLinks.slice(2).map((link) => (
              <Link key={link.name} to={link.path} className="text-[11px] tracking-[0.2em] uppercase text-linen/60 hover:text-ember transition-all duration-300 font-semibold relative group">
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-ember transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>
          <button 
            onClick={onOpenCart}
            className="relative text-linen/80 hover:text-ember transition-colors p-2 -mr-2 group focus:outline-none focus:ring-2 focus:ring-ember/20 rounded-full"
            aria-label={`Open cart, ${cartCount} items`}
          >
            <SafeIcon icon={FiShoppingBag} className="w-5 h-5" />
            {cartCount > 0 && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-0 right-0 bg-ember text-linen text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-lg"
              >
                {cartCount}
              </motion.span>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-full left-0 w-full bg-smoke/95 backdrop-blur-2xl border-b border-white/10 py-10 px-8 flex flex-col space-y-8 shadow-2xl"
          >
            {navLinks.map((link) => (
              <Link key={link.name} to={link.path} className="text-2xl tracking-widest uppercase text-linen hover:text-ember transition-colors font-serif border-b border-white/5 pb-4">
                {link.name}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}